import React, { useEffect, useState } from "react";
import { api } from "@/AuthContext";
import { useAuth } from "@/AuthContext";
import { toast } from "sonner";
import { Plus, X, DownloadSimple } from "@phosphor-icons/react";
import ReassignDialog from "../components/ReassignDialog";
import { scopedCompanies, defaultCompanyId } from "@/lib/companyScope";
import ExportButton from "../components/ExportButton";
import ObligationGroups from "../components/obligations/ObligationGroups";
import useCatalog from "../hooks/useCatalog";

export default function Obligations() {
  const { user, can } = useAuth();
  const [items, setItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [reassignId, setReassignId] = useState(null);
  const [focus, setFocus] = useState(null);
  const isAdmin = ["super_admin", "admin"].includes(user?.role);

  const reload = () => api.get("/obligations").then(r=>setItems(r.data));
  useEffect(()=>{
    reload();
    api.get("/installations").then(r=>setInstallations(r.data));
    api.get("/companies").then(r=>setCompanies(r.data));
  },[user]);

  const onDelete = async (id) => {
    if(!window.confirm("¿Eliminar obligación?")) return;
    try{ await api.delete(`/obligations/${id}`); toast.success("Eliminada"); reload(); }
    catch(e){ toast.error(e?.response?.data?.detail || "Error"); if (e?.response?.status === 409 && isAdmin) setReassignId(id); }
  };

  const canEdit = can("obligations", "write");
  const canDelete = can("obligations", "delete");

  const [seeding, setSeeding] = useState(false);
  const seedDefaults = async () => {
    if (!window.confirm("¿Cargar la plantilla oficial de obligaciones GLP (CNE, ASEA, SENER)? Se agregarán 61 obligaciones a la empresa activa. Las duplicadas se omitirán.")) return;
    setSeeding(true);
    try {
      const r = await api.post("/obligations/seed-defaults");
      toast.success(`Plantilla cargada: ${r.data.inserted} nuevas, ${r.data.skipped} existentes`);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al cargar plantilla");
    } finally { setSeeding(false); }
  };

  return (
    <div className="space-y-6" data-testid="obligations-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-neutral-500 font-semibold mb-2">Cumplimiento</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Obligaciones</h1>
          <p className="text-neutral-500 mt-2">Obligaciones regulatorias por empresa, instalación y autoridad.</p>
        </div>
        {canEdit ? (
          <div className="flex gap-2 flex-wrap">
            <ExportButton resource="obligations"/>
            {user?.role === "super_admin" && (
              <button
                onClick={seedDefaults}
                disabled={seeding}
                className="inline-flex items-center gap-2 px-4 py-3 border border-neutral-300 bg-white hover:bg-neutral-50 text-neutral-700 font-semibold rounded-md text-sm disabled:opacity-60"
                title="Cargar plantilla oficial de obligaciones para el sector GLP"
                data-testid="seed-defaults-btn"
              >
                <DownloadSimple size={16} weight="bold"/> {seeding ? "Cargando..." : "Cargar plantilla GLP"}
              </button>
            )}
            <button onClick={()=>{setEditing(null); setOpen(true);}} className="inline-flex items-center gap-2 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-md" data-testid="new-obligation-btn">
              <Plus size={18} weight="bold"/> Nueva obligación
            </button>
          </div>
        ) : <ExportButton resource="obligations"/>}
      </div>
      <ObligationGroups user={user} items={items} focus={focus} companies={companies} installations={installations}
        actions={{ canEdit, canDelete, isAdmin, onEdit: (o) => { setEditing(o); setOpen(true); }, onReassign: setReassignId, onDelete }}/>
      {reassignId && <ReassignDialog kind="obligation" sourceId={reassignId} onClose={()=>setReassignId(null)} onDone={()=>{setReassignId(null); reload();}}/>}
      {open && <Form user={user} companies={companies} installations={installations} initial={editing} onClose={()=>setOpen(false)} onSuccess={(o)=>{setOpen(false); setFocus(o); reload();}}/>}
    </div>
  );
}

// Conserva el valor actual aunque no exista en el catálogo (datos heredados)
const withCurrent = (list, value) => (!value || list.some(x => x.name === value)) ? list : [...list, { id: `cur-${value}`, name: value }];

function Form({user, companies, installations, initial, onClose, onSuccess}){
  const authorities = useCatalog("autoridad");
  const categoriesCat = useCatalog("categoria");
  const frequencies = useCatalog("frecuencia_notificacion");
  const [f,setF] = useState(initial ? { ...initial, company_id: initial.company_id || defaultCompanyId(user) } : {name:"", company_id: defaultCompanyId(user), installation_id:"", authority:"", category:"", frequency:"", description:"", responsible:""});
  // initialize defaults from catalogs once they arrive
  useEffect(()=>{
    if (initial) return;
    setF(prev => ({
      ...prev,
      authority: prev.authority || (authorities[0]?.name || ""),
      category: prev.category || (categoriesCat[0]?.name || ""),
      frequency: prev.frequency || (frequencies[0]?.name || ""),
    }));
  }, [authorities, categoriesCat, frequencies, initial]);
  const [busy,setBusy] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const insts = installations.filter(i=>!f.company_id||i.company_id===f.company_id);
  const companyOpts = scopedCompanies(user, companies);
  const save = async ()=>{
    if(!f.name) return toast.error("Nombre requerido");
    if(!f.company_id) return toast.error("Empresa requerida");
    setBusy(true);
    try{
      const r = initial ? await api.put(`/obligations/${initial.id}`,f) : await api.post("/obligations",f);
      toast.success("Guardado"); onSuccess(r.data);
    }catch(e){ toast.error(e?.response?.data?.detail||"Error"); }finally{setBusy(false);}
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-md w-full max-w-xl border border-neutral-200 anim-fade-up max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center p-5 border-b border-neutral-200">
          <h2 className="font-display text-xl font-bold">{initial?"Editar":"Nueva"} obligación</h2>
          <button onClick={onClose}><X size={20}/></button>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-3">
          {(user.role==="super_admin" || companyOpts.length > 1) && (
            <div className="md:col-span-2"><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Empresa</label>
              <select value={f.company_id} onChange={e=>setF({...f,company_id:e.target.value,installation_id:""})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="obl-form-company">
                <option value="">Seleccionar...</option>{companyOpts.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
          )}
          <div className="md:col-span-2"><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Nombre de la obligación</label>
            <input value={f.name} onChange={e=>setF({...f,name:e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="obl-form-name"/></div>
          <div><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Autoridad</label>
            <select value={f.authority} onChange={e=>setF({...f,authority:e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="obl-form-authority">
              <option value="">Seleccionar...</option>
              {withCurrent(authorities, f.authority).map(a=><option key={a.id} value={a.name}>{a.name}</option>)}</select></div>
          <div><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Categoría</label>
            <select value={f.category} onChange={e=>setF({...f,category:e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="obl-form-category">
              <option value="">Seleccionar...</option>
              {withCurrent(categoriesCat, f.category).map(c=><option key={c.id} value={c.name}>{c.name}</option>)}</select></div>
          <div><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Frecuencia</label>
            <select value={f.frequency} onChange={e=>setF({...f,frequency:e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="obl-form-frequency">
              <option value="">Seleccionar...</option>
              {withCurrent(frequencies, f.frequency).map(fq=><option key={fq.id} value={fq.name}>{fq.name}</option>)}</select></div>
          <div><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Instalación (opcional)</label>
            <select value={f.installation_id||""} onChange={e=>setF({...f,installation_id:e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md bg-white" data-testid="obl-form-installation">
              <option value="">— Todas / Empresa general —</option>{insts.map(i=><option key={i.id} value={i.id}>{i.name}</option>)}</select></div>
          <div className="md:col-span-2"><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Responsable</label>
            <input value={f.responsible||""} onChange={e=>setF({...f,responsible:e.target.value})} className="w-full px-3 py-2.5 border border-neutral-200 rounded-md" data-testid="obl-form-responsible"/></div>
          <div className="md:col-span-2"><label className="block text-xs uppercase tracking-widest font-semibold text-neutral-700 mb-2">Descripción</label>
            <textarea value={f.description||""} onChange={e=>setF({...f,description:e.target.value})} rows={2} className="w-full px-3 py-2 border border-neutral-200 rounded-md"/></div>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-neutral-200 bg-neutral-50">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-neutral-300 rounded-md">Cancelar</button>
          <button onClick={save} disabled={busy} className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md disabled:opacity-60" data-testid="obl-form-submit">{busy?"Guardando...":"Guardar"}</button>
        </div>
      </div>
    </div>
  );
}
